import { useEffect } from 'react';

const SITE_DOMAIN = process.env.REACT_APP_SITE_URL || 'https://acem-fes.com';
const DEFAULT_TITLE = 'ACEM Fès – Association des Comoriens Étudiant au Maroc, Section Régionale de Fès';
const DEFAULT_DESCRIPTION = 'Plateforme officielle de l’Association des Comoriens Étudiant au Maroc (ACEM) section régionale de Fès. Démarches administratives, bibliothèque digitale, événements, communiqués officiels et vie associative.';
const DEFAULT_IMAGE = `${SITE_DOMAIN}/logo.png`;

export default function SEO({
  title,
  description,
  canonical,
  noindex = false,
  image,
  type = 'website',
  jsonLd,
}) {
  useEffect(() => {
    // 1. Title
    const fullTitle = title ? `${title} | ACEM Fès` : DEFAULT_TITLE;
    document.title = fullTitle;

    // 2. Helper to set or create meta element
    const setMetaTag = (nameAttr, nameValue, content) => {
      if (!content) return;
      let element = document.querySelector(`meta[${nameAttr}="${nameValue}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(nameAttr, nameValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // 3. Helper to set or create link element
    const setLinkTag = (rel, href) => {
      if (!href) return;
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Meta Basic
    const metaDescription = description || DEFAULT_DESCRIPTION;
    setMetaTag('name', 'description', metaDescription);
    setMetaTag('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');

    // Canonical URL
    const canonicalUrl = canonical
      ? canonical.startsWith('http')
        ? canonical
        : `${SITE_DOMAIN}${canonical.startsWith('/') ? canonical : `/${canonical}`}`
      : `${SITE_DOMAIN}${window.location.pathname}`;

    setLinkTag('canonical', canonicalUrl);

    // Open Graph
    const ogImage = image
      ? image.startsWith('http')
        ? image
        : `${SITE_DOMAIN}${image.startsWith('/') ? image : `/${image}`}`
      : DEFAULT_IMAGE;

    setMetaTag('property', 'og:title', fullTitle);
    setMetaTag('property', 'og:description', metaDescription);
    setMetaTag('property', 'og:type', type);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:image', ogImage);
    setMetaTag('property', 'og:site_name', 'ACEM Fès');
    setMetaTag('property', 'og:locale', 'fr_FR');

    // Twitter Card
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', fullTitle);
    setMetaTag('name', 'twitter:description', metaDescription);
    setMetaTag('name', 'twitter:image', ogImage);

    // Schema.org JSON-LD
    let scriptElement = document.getElementById('seo-jsonld');
    if (jsonLd) {
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.id = 'seo-jsonld';
        scriptElement.type = 'application/ld+json';
        document.head.appendChild(scriptElement);
      }
      scriptElement.text = JSON.stringify(jsonLd);
    } else if (scriptElement) {
      scriptElement.remove();
    }
  }, [title, description, canonical, noindex, image, type, jsonLd]);

  return null;
}
